import { StarIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { InfoWithLabel } from "@/core/components/info-with-label";
import { Button } from "@/core/components/ui/button";
import { DialogContent } from "@/core/components/ui/dialog";
import { Dialog } from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { ReviewAttributionContent } from "@/activity-feed/activity-event-modal";
import { REVIEW_PLATFORM_TO_DISPLAY } from "@/activity-feed/constants";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { useBusiness } from "@/core/business";
import { formatDate } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import { useGetReview } from "@/reviews/query";
import { normalizeRating } from "@/reviews/utils";
import { ReviewsSidebarSkeleton } from "./reviews-sidebar-skeleton";
import FacebookLogoSrc from "@/assets/images/facebook-logo.png";
import GoogleLogoSrc from "@/assets/images/google-logo.png";
import TripAdvisorLogoSrc from "@/assets/images/trip-advisor-logo.jpeg";
import YelpLogoSrc from "@/assets/images/yelp-icon.png";

const PLATFORM_TO_ICON: Record<string, string> = {
  google: GoogleLogoSrc,
  facebook: FacebookLogoSrc,
  yelp: YelpLogoSrc,
  tripadvisor: TripAdvisorLogoSrc,
};

interface ReviewsSidebarProps {
  reviewId: string;
  onClose: () => void;
}

export const ReviewsSidebar = ({ reviewId, onClose }: ReviewsSidebarProps) => {
  const { businessId } = useBusiness();
  const { data: review, isLoading } = useGetReview(businessId, reviewId);

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (isLoading) {
    return <ReviewsSidebarSkeleton />;
  }

  if (!review) {
    return (
      <BusinessSettingsSidebarWrapper>
        <div className="p-5">Review not found.</div>
      </BusinessSettingsSidebarWrapper>
    );
  }

  const platformIconSrc = review.platform
    ? PLATFORM_TO_ICON[review.platform]
    : undefined;

  const rating = normalizeRating(review.rating);

  const dateStr = review.external_created_at || review.created_at;

  const getAttributionText = () => {
    const { technicians } = review;
    if (!technicians || technicians.length === 0) return null;

    return technicians
      .map((t) => t.display_identification)
      .filter(Boolean)
      .join(", ");
  };

  const attributionText = getAttributionText();

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText="Review"
        name={review.reviewer_name}
        onClose={onClose}
      />
      <Stack className="bg-bg-secondary/50 p-6 pt-0">
        <Button
          onClick={() => setIsModalOpen(true)}
          fullWidth={true}
          size={"sm"}
        >
          Attribute
        </Button>
      </Stack>
      <Stack className="flex-1 overflow-auto px-6 py-4" fullWidth>
        <Stack gap="md" fullWidth>
          <InfoWithLabel label="Date">{formatDate(dateStr)}</InfoWithLabel>

          {review.platform && (
            <InfoWithLabel label="Platform">
              <Stack direction="row" align="center" gap="sm">
                {platformIconSrc && (
                  <img
                    src={platformIconSrc}
                    alt={`${review.platform || "Google"} logo`}
                    className="h-4 w-4 object-contain"
                  />
                )}
                <Typography variant="bodyW1">
                  {REVIEW_PLATFORM_TO_DISPLAY[
                    review.platform as keyof typeof REVIEW_PLATFORM_TO_DISPLAY
                  ] || review.platform}
                </Typography>
              </Stack>
            </InfoWithLabel>
          )}

          <InfoWithLabel label="Rating">
            <Stack direction="row" gap="xs" align="center">
              {Array.from({ length: 5 }).map((_, i) => (
                <StarIcon
                  key={`star-icon-${i}`}
                  className={cn("h-4 w-4 text-gold-30", {
                    "opacity-30": i >= rating,
                  })}
                />
              ))}
            </Stack>
          </InfoWithLabel>

          {review.body && (
            <InfoWithLabel label="Review">
              <Typography variant="bodyW1" textColor="secondary">
                {review.body}
              </Typography>
            </InfoWithLabel>
          )}

          {attributionText && (
            <InfoWithLabel label="Attribution">
              <Typography variant="bodyW1" textColor="secondary">
                {attributionText}
              </Typography>
            </InfoWithLabel>
          )}
        </Stack>
      </Stack>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <ReviewAttributionContent
            reviewId={review.id}
            onClose={() => setIsModalOpen(false)}
          ></ReviewAttributionContent>
        </DialogContent>
      </Dialog>
    </BusinessSettingsSidebarWrapper>
  );
};
